/**
 * GifCodecSearch — pick encoder settings for a byte budget (Target mode) or
 * for a quality floor (Quick mode), measuring instead of guessing.
 *
 * The search talks to a RUNNER, not to the encoder:
 *   runner.encode(params, {proxy:boolean, keep:boolean}) -> Promise<{size, quality:{s2,de}, bytes?}>
 *   runner.parallel    how many encodes may run at once
 *   runner.proxyShare  share of the frames the proxy holds
 *   runner.sample      a long clip only: {share, frameShare} of the sample that
 *                      runner.encode(params, {sample:true}) encodes in place of the file
 * In the browser the runner is a pool of codec workers (gif-codec-pool.js);
 * in Node tests it is a GifCodec.Session called directly — same search code.
 *
 * Strength q is one number: the encoder's motion and lossy allowances, in dE
 * (q = 0.5 is near-lossless, 9 is heavy). Measured on 11 real GIFs, equal
 * motion and lossy sits on the size/quality frontier (docs/why/gif-codec.md).
 *
 * LANES. The full lane keeps every pixel and up to 255 colours, with error
 * diffusion. Other lanes — a smaller frame (0.85 … 0.18 of the width, only
 * when the user allowed it), a smaller palette (128 / 64 / 32 colours), and
 * the same without diffusion (posterised: flat or synthetic art pays for
 * dithering noise and gains nothing from it — a test card at 32 colours went
 * 0.58 → 0.45 of its size at higher quality) — are tried only when the full
 * lane is predicted to look poor at the budget or cannot reach it.
 *
 * WHICH RESULT WINS. Every real encode that fits the budget is a candidate;
 * the one that MEASURES best (SSIMULACRA 2 on the real file) is delivered —
 * not the biggest one: a game clip at 0.4 of its size measured 42 at full
 * frame and 62 at 0.8 of the width, and "biggest that fits" had picked the 42.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.GifCodecSearch = api;
}(typeof self !== 'undefined' ? self : typeof globalThis !== 'undefined' ? globalThis : null, function () {
    'use strict';

    var Q_MIN = 0.5, Q_MAX = 9;
    var PROXY_QS = [0.5, 2, 5];
    var SCALES = [0.85, 0.7, 0.55, 0.42, 0.32, 0.24, 0.18];
    var PALETTES = [128, 64, 32];
    var DITHER = 0.75;          // error diffusion of a lane; 0 = posterise (flat and synthetic art: no noise to pay for)
    var LANE_QS = [1.5, 5];     // proxy strengths per extra lane
    var LANE_GAIN = 3;          // another lane must be predicted this much better (SSIMULACRA 2) to be tried
    var GOOD_ENOUGH = 70;       // full lane predicted at least this good -> do not even try others
    var FILL = 0.92;            // share of the budget that counts as "filled"
    var MAX_LANES = 3;          // lanes refined on the real file at most
    var MAX_FULL = 6;           // real encodes after which a search that HAS a fitting result stops: 43 of
                                // 46 searches on real files needed ≤ 5; the rest spent 7-9 choosing
                                // between scores a few points apart (a screen recording at 0.06: −23 vs −27)
    var NEAR_TIE = 0.25;        // measured scores closer than this are a tie: the bigger file wins
    var FLOOR_RATIO = 6;        // the full frame measured this many times the budget → measure the floor first
    var MAX_FULL_LONG = 2;      // a long clip (searched on its sample): whole-file encodes after which a result
                                // that fits is delivered — the first lands where the sample said, the second
                                // corrects it by what the file measured; a third only trimmed 0.97 to 0.98
    var FILL_LONG = 0.88;       // …and the share of the budget that counts as filled there: the step from 0.9 to
                                // 0.97 is a point or two of score and one more pass over the whole clip
    var SLACK = 1.25;           // how far over the budget a PROXY's curve may promise before a weaker probe is skipped
    var SLACK_LONG = 0.97;      // …and a sample's curve before a WHOLE-FILE pass is spent on one: only where the
                                // curve says the weaker strength clearly still fits (flat, posterised art). At
                                // 1.03 two of five real searches spent a pass on a probe that came out 7 % and
                                // 93 % over the budget — the second off a curve of a single point
    var MAX_SAMPLE = 9;         // …and sample encodes after which the search ON THE SAMPLE stops choosing (the file's
                                // MAX_FULL is 6): three lanes get three each. At 6 one lane's steep stretch took four,
                                // and the lanes beside it were never encoded at a strength that fits
    var LONG_AIM = 0.97;        // the sample is searched for this much of its part of the budget. On 16 searches of
                                // real clips the file weighed 0.91-1.04 of what its sample said; three came out
                                // 0.4-5 % over and cost a second whole-file encode. A lower aim (0.95) is not the
                                // cure: on the modelled clip it moved the choice to a smaller frame that could not
                                // fill the budget — a second encode again, for a different reason

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    function lane(scale, K, diffuse) { return { scale: scale, K: K, diffuse: diffuse, key: scale + ':' + K + ':' + diffuse }; }
    var BASE = lane(1, 255, DITHER);

    /** the lane that can go smallest: smallest frame, then fewest colours, then no dithering noise */
    function floorLane(list) {
        return list.slice().sort(function (a, b) { return (a.scale - b.scale) || (a.K - b.K) || (a.diffuse - b.diffuse); })[0];
    }

    /** the end segment (a, b) extended to x: log-linear for size, linear for scores; flat if it rises */
    function extend(a, b, x, key) {
        if (b.q <= a.q || a[key] == null || b[key] == null) return x < a.q ? a[key] : b[key];
        var from = x < a.q ? a : b;
        if (key === 'size') {
            var k = (Math.log(b.size) - Math.log(a.size)) / (b.q - a.q);
            return k < 0 ? Math.exp(Math.log(from.size) + k * (x - from.q)) : from.size;
        }
        var g = (b[key] - a[key]) / (b.q - a.q);
        return g < 0 ? Math.min(100, from[key] + g * (x - from.q)) : from[key];
    }

    /**
     * Interpolation over q (log-linear for size). Beyond the measured strengths the
     * end segments are extended. Holding the end value flat promised a lane a score
     * of 57 at q 8.9 where the file measured 30 (and ranked it first); at the weak
     * end it priced a smaller frame that fits at q 0.8 as if it needed q 1.5 — the
     * weakest a lane's proxy is encoded at — and the search skipped the best lane.
     */
    function interp(pts, x, key) {
        if (!pts || !pts.length) return null;
        var s = pts.slice().sort(function (a, b) { return a.q - b.q; });
        if (x <= s[0].q) return s.length > 1 ? extend(s[0], s[1], x, key) : s[0][key];
        var z = s[s.length - 1];
        if (x >= z.q) return s.length > 1 ? extend(s[s.length - 2], z, x, key) : z[key];
        for (var i = 0; i < s.length - 1; i++) {
            if (x >= s[i].q && x <= s[i + 1].q) {
                var f = (x - s[i].q) / (s[i + 1].q - s[i].q || 1);
                if (key === 'size') return Math.exp(Math.log(s[i].size) + f * (Math.log(s[i + 1].size) - Math.log(s[i].size)));
                if (s[i][key] == null || s[i + 1][key] == null) return s[i][key] != null ? s[i][key] : s[i + 1][key];
                return s[i][key] + f * (s[i + 1][key] - s[i][key]);
            }
        }
        return s[s.length - 1][key];
    }

    /** strength that lands a (decreasing) size curve on `size`; null if out of reach */
    function qForSize(pts, size) {
        var s = pts.slice().sort(function (a, b) { return a.q - b.q; });
        if (!s.length) return null;
        if (size >= s[0].size) {
            // weaker than the weakest point: extend the first segment (down to Q_MIN)
            if (s.length < 2) return s[0].q;
            var k0 = (Math.log(s[1].size) - Math.log(s[0].size)) / (s[1].q - s[0].q || 1);
            return k0 < -1e-6 ? Math.max(Q_MIN, s[0].q + (Math.log(size) - Math.log(s[0].size)) / k0) : s[0].q;
        }
        var last = s[s.length - 1];
        if (size < last.size) {
            if (s.length < 2) return null;
            var a = s[s.length - 2], b = last;
            var slope = (Math.log(b.size) - Math.log(a.size)) / (b.q - a.q || 1);
            if (slope >= -1e-6) return null;
            var q = b.q + (Math.log(size) - Math.log(b.size)) / slope;
            return q <= Q_MAX ? q : null;
        }
        for (var i = 0; i < s.length - 1; i++) {
            var p = s[i], n = s[i + 1];
            if (size <= p.size && size >= n.size) {
                var f = (Math.log(p.size) - Math.log(size)) / (Math.log(p.size) - Math.log(n.size) || 1);
                return p.q + f * (n.q - p.q);
            }
        }
        return null;
    }

    /** does fitting result a look better than b (measured; near-ties go to the bigger file) */
    function better(a, b) {
        if (!b) return true;
        var sa = a.s2 == null ? -Infinity : a.s2, sb = b.s2 == null ? -Infinity : b.s2;
        if (Math.abs(sa - sb) > NEAR_TIE) return sa > sb;
        return a.size > b.size;
    }

    function Search(runner, info, opts) {
        this.r = runner; this.info = info; this.opts = opts || {};
        this.full = {};       // lane key -> [{q, size, s2, de, lane, res}] (res kept only for the best)
        this.prox = {};       // lane key -> [{q, size, s2}]
        this.best = null;     // the fitting result that measures best so far
        this.s2Offset = 0;    // full-file score minus proxy score at the same strength
        this.encodes = 0; this.proxyEncodes = 0; this.sampleEncodes = 0;
        this.asked = {};      // lane key -> strengths a proxy was requested at (done or in flight)
        this.maxFull = MAX_FULL; this.fill = FILL; this.slack = SLACK;
    }
    Search.prototype.cancelled = function () { return !!(this.opts.isCancelled && this.opts.isCancelled()); };
    Search.prototype.progress = function (f, stage) {
        if (this.encodeSpan && stage !== 'encode') return;      // refine()'s own marks belong to a search on the file
        if (this.opts.onProgress) this.opts.onProgress(clamp(f, 0, 1), stage);
    };

    Search.prototype.proxyAt = function (ln, qs) {
        var self = this, asked = (this.asked[ln.key] = this.asked[ln.key] || []);
        qs.forEach(function (q) { asked.push(q); });
        return Promise.all(qs.map(function (q) {
            return self.r.encode({ scale: ln.scale, K: ln.K, diffuse: ln.diffuse, q: q }, { proxy: true }).then(function (res) {
                self.proxyEncodes++;
                var pt = { q: q, size: res.size, s2: res.quality ? res.quality.s2 : null };
                (self.prox[ln.key] = self.prox[ln.key] || []).push(pt);
                if (res.colors && ln.scale === 1 && ln.K === 255) self.colors = res.colors;
                return pt;
            });
        }));
    };

    /** one real encode; a fitting result that measures best keeps its bytes, every other drops them */
    Search.prototype.fullAt = function (ln, q, target) {
        var self = this, first = !(this.full[ln.key] && this.full[ln.key].length);
        // a whole-file encode of a long clip takes minutes: its own progress moves the bar (see forTargetSampled)
        var o = { proxy: false, keep: true }, at = this.encodes;
        if (this.encodeSpan) o.onProgress = function (f) { self.progress(self.encodeSpan[0] + (Math.min(at, 2) + clamp(f, 0, 1)) * self.encodeSpan[1], 'encode'); };
        return this.r.encode({ scale: ln.scale, K: ln.K, diffuse: ln.diffuse, q: q }, o).then(function (res) {
            self.encodes++;
            var pt = { q: q, size: res.size, s2: res.quality ? res.quality.s2 : null, de: res.quality ? res.quality.de : null, lane: ln, res: res };
            (self.full[ln.key] = self.full[ln.key] || []).push(pt);
            if (target && res.size <= target && better(pt, self.best)) {
                if (self.best) self.best.res = null;
                self.best = pt;
            } else if (target) pt.res = null;
            // The first real encode of a lane calibrates the proxy→file factor of its whole
            // family: if it lies outside the strengths the proxy was encoded at, the proxy is
            // encoded at that strength too, so the factor is a measured pair, not a guess.
            return first && target && self.outside(ln, q) ? self.proxyAt(ln, [q]).then(function () { return pt; }) : pt;
        });
    };

    /** is q further than half a step outside the strengths a lane's proxy was encoded at? */
    Search.prototype.outside = function (ln, q) {
        var p = this.asked[ln.key];   // requested, not only finished: a twin in flight counts
        if (!p || !p.length) return false;
        var lo = Infinity, hi = -Infinity;
        p.forEach(function (x) { if (x < lo) lo = x; if (x > hi) hi = x; });
        return q > hi + 0.5 || q < lo - 0.5;
    };

    /**
     * Proxy size → file size factor of a lane: from its own real encodes, else from
     * the nearest lane of its FAMILY that has some (frame size with frame size,
     * palette with palette), else from the full lane. A factor per family was ~10×
     * more accurate than one for all (gifsicle's families, 2026-08-17) — and it is
     * how a miss teaches the search: once one smaller-frame lane has been encoded for
     * real, every other frame size is predicted from it, not from the full frame.
     */
    Search.prototype.skew = function (ln) {
        if (!this.prox[ln.key]) return null;
        var self = this, f = this.full[ln.key];
        if (!f || !f.length) {
            var near = null, bd = Infinity;
            Object.keys(this.full).forEach(function (k) {
                var pts = self.full[k];
                if (!pts.length || !self.prox[k]) return;
                var o = pts[0].lane;
                if ((o.scale < 1) !== (ln.scale < 1) || (o.K < 255) !== (ln.K < 255)) return;
                var dist = Math.abs(Math.log(o.scale / ln.scale)) + Math.abs(Math.log(o.K / ln.K)) + (o.diffuse === ln.diffuse ? 0 : 1);
                if (dist < bd) { bd = dist; near = pts; }
            });
            f = near || this.full[BASE.key];
        }
        if (!f || !f.length) return null;
        var sum = 0, n = 0;
        for (var i = 0; i < f.length; i++) {
            var ps = interp(this.prox[f[i].lane.key], f[i].q, 'size');
            if (ps) { sum += Math.log(f[i].size / ps); n++; }
        }
        return n ? Math.exp(sum / n) : null;
    };

    /** predicted full-file curve of a lane: proxy curve × skew */
    Search.prototype.predicted = function (ln) {
        var k = this.skew(ln), p = this.prox[ln.key];
        if (!k || !p) return null;
        return p.map(function (x) { return { q: x.q, size: x.size * k, s2: x.s2 }; });
    };

    /** predicted strength at which a lane meets the budget, or null when it cannot */
    Search.prototype.qAt = function (ln, target) {
        var p = this.predicted(ln);
        return p ? qForSize(p, target * 0.97) : null;
    };

    /** predicted full-file score of a lane at the budget, or null when it cannot reach it */
    Search.prototype.scoreAt = function (ln, target) {
        var q = this.qAt(ln, target);
        if (q == null) return null;
        var v = interp(this.prox[ln.key], clamp(q, Q_MIN, Q_MAX), 's2');
        return v == null ? null : v + this.s2Offset;
    };

    /**
     * Best result that fits targetBytes.
     * @returns Promise<{bytes, size, quality, lane, q, encodes, proxyEncodes, width, height} | {unreachable:true, minSize}>
     */
    Search.prototype.forTarget = function (target) {
        return this.r.sample && !this.opts.onFile ? this.forTargetSampled(target) : this.forTargetOnFile(target);
    };

    /**
     * A LONG clip. Every real encode of forTargetOnFile is the whole file — 3 to 6 of them, and
     * on a 1080p clip of 300 frames that was eighteen minutes. Here the same search runs on the
     * runner's SAMPLE (a few runs of consecutive frames at the full frame, a fraction of the
     * clip) against the sample's part of the budget, and the file is encoded only to deliver:
     *   1. at the lane and strength the sample chose. On six real clips the file weighed
     *      0.96-1.04 of what its sample said, so this one usually is the result;
     *   2. when it is not — over the budget, or under FILL_LONG of it — the budget the sample was
     *      searched for was not the file's, and with it, possibly, the lane: the sample is
     *      searched once more for what the file just measured (its encodes are remembered, so
     *      this costs a few new ones) and the file is encoded at that choice;
     *   3. inside the lane, refine() trims along the sample's own curve of it, scaled by the
     *      whole-file sizes (the curve stands where a proxy's stands for a short clip).
     * Once something fits, MAX_FULL_LONG whole-file encodes are the end of it.
     */
    Search.prototype.forTargetSampled = function (target) {
        var self = this, r = this.r, sm = r.sample, memo = {}, counts = { p: 0, s: 0 };
        var runner = {
            parallel: r.parallel,
            get proxyShare() { return (r.proxyShare || 0.1) / (sm.frameShare || 1); },     // the proxy's share of the SAMPLE
            encode: function (params, o) {
                var kind = o && o.proxy ? 'p' : 's';
                var key = kind + [params.scale, params.K, params.diffuse, params.q].join(':');
                if (!memo[key]) { counts[kind]++; memo[key] = r.encode(params, kind === 'p' ? o : { sample: true }); }
                return memo[key];
            }
        };
        // weight: how much heavier the sample reads than its share of the source bytes says —
        // assumed 1 with a margin (LONG_AIM) until a whole-file encode has measured it
        var weight = 1, aim = LONG_AIM, again = false;
        function finish() {
            self.proxyEncodes = counts.p; self.sampleEncodes = counts.s;
            return self.finish();
        }
        function pass() {
            var inner = new Search(runner, self.info, { keepDims: self.opts.keepDims, isCancelled: self.opts.isCancelled, onFile: true,
                onProgress: again ? null : function (f, stage) { self.progress(f * 0.6, stage); } });
            inner.maxFull = MAX_SAMPLE;
            return inner.forTargetOnFile(target * sm.share * weight * aim).then(function (pick) {
                self.proxyEncodes = counts.p; self.sampleEncodes = counts.s;
                if (!pick || self.cancelled()) return null;
                if (pick.unreachable) {
                    return self.best ? finish() : { unreachable: true, minSize: Math.round(pick.minSize / (sm.share * weight * aim)), encodes: self.encodes };
                }
                var ln = pick.lane, curve = inner.full[ln.key].map(function (pt) { return { q: pt.q, size: pt.size, s2: pt.s2 }; });
                self.prox[ln.key] = curve;
                self.asked[ln.key] = curve.map(function (pt) { return pt.q; });
                self.maxFull = MAX_FULL_LONG; self.fill = FILL_LONG; self.slack = SLACK_LONG;
                self.encodeSpan = [0.62, 0.12];        // whole-file encodes (two, three when nothing fitted yet) share 0.62 … 0.98 of the bar
                function trim() {
                    return self.refine(ln, target).then(function () {
                        if (self.best || self.cancelled()) return finish();
                        // the file would not go where its sample went: the ladder of smaller lanes, on the file
                        return self.fallback(target, inner.lanes || [ln]);
                    });
                }
                // the second search may land on what the file was already encoded at: nothing new to learn there
                if ((self.full[ln.key] || []).some(function (pt) { return Math.abs(pt.q - pick.q) < 0.02; })) return trim();
                self.progress(0.62 + Math.min(self.encodes, 2) * 0.12, 'encode');
                return self.fullAt(ln, pick.q, target).then(function (pt) {
                    if (self.cancelled()) return null;
                    var onSample = interp(curve, pt.q, 'size');
                    if (!again && onSample && pt.size && (pt.size > target || pt.size < target * FILL_LONG)) {
                        again = true; weight = onSample / (sm.share * pt.size); aim = 1;
                        return pass();
                    }
                    return trim();
                });
            });
        }
        return pass();
    };

    Search.prototype.forTargetOnFile = function (target) {
        var self = this, info = this.info, lanes = [BASE, lane(1, 255, 0)];
        this.lanes = lanes;
        if (!this.opts.keepDims) {
            SCALES.forEach(function (sc) { if (info.width * sc >= 48 && info.height * sc >= 24) lanes.push(lane(sc, 255, DITHER)); });
        }
        this.progress(0.02, 'proxy');
        return this.proxyAt(BASE, PROXY_QS).then(function () {
            if (self.cancelled()) return null;
            // A smaller palette is a lane only below the source's own colour count: a
            // 32-colour sticker encodes identically at 255, 128, 64 and 32 colours.
            var colors = self.colors;
            PALETTES.forEach(function (k) { if (!colors || k < colors) lanes.push(lane(1, k, DITHER), lane(1, k, 0)); });
            // first full encode, aimed with a skew guess (the proxy holds ~share of the frames)
            var guess = 1 / Math.max(0.02, self.r.proxyShare || 0.1);
            var curve = self.prox[BASE.key].map(function (x) { return { q: x.q, size: x.size * guess }; });
            var q0 = qForSize(curve, target * 0.97);
            self.progress(0.12, 'calibrate');
            q0 = q0 == null ? 5 : clamp(q0, Q_MIN, Q_MAX);
            // its proxy twin (when q0 lies outside the strengths measured) runs alongside, not after
            var twin = self.outside(BASE, q0) ? self.proxyAt(BASE, [q0]) : null;
            return Promise.all([self.fullAt(BASE, q0, target), twin]).then(function (r) { return r[0]; });
        }).then(function (first) {
            if (!first || self.cancelled()) return null;
            // Proxy scores are biased (few, run-end frames): shift them by what the first
            // full encode measured against the proxy at the same strength.
            var ps0 = interp(self.prox[BASE.key], first.q, 's2');
            self.s2Offset = (first.s2 != null && ps0 != null) ? first.s2 - ps0 : 0;
            // A budget far under what the full frame just measured may lie under anything this file
            // can become. The floor — the smallest lane at full strength — is one small encode, so it
            // is measured before any scouting: a 1.5 MB clip asked for 1 KB used to search for two
            // minutes before the fallback could say «that small is impossible». A floor that fits is
            // a real point of its family too (the scale lanes' proxy factor comes from it).
            var floor = first.size > target * FLOOR_RATIO ? self.fullAt(floorLane(lanes), Q_MAX, target) : Promise.resolve(null);
            return floor.then(function (fl) {
                if (self.cancelled()) return null;
                if (fl && fl.size > target) return { unreachable: true, minSize: fl.size, encodes: self.encodes };
                var s2base = self.scoreAt(BASE, target);
                if (lanes.length < 2 || (s2base != null && s2base >= GOOD_ENOUGH)) {
                    return self.tryLanes([BASE], target, lanes);
                }
                self.progress(0.3, 'lanes');
                return self.exploreLanes(target, lanes, s2base).then(function () {
                    return self.tryLanes(lanes, target, lanes);
                });
            });
        });
    };

    /**
     * Score the other lanes on the proxy in waves instead of all at once (a median
     * 17-23 proxy encodes per search, all lanes included, where scoring every lane
     * took 31): the middle of each family first, then further along a family only
     * while its trend still improves — at a given budget, quality against frame
     * size (and against palette size) has one peak.
     */
    Search.prototype.exploreLanes = function (target, lanes, s2base) {
        var self = this, scored = {};
        var scales = lanes.filter(function (l) { return l.scale < 1; });
        function find(scale, K, d) { return lanes.filter(function (l) { return l.scale === scale && l.K === K && l.diffuse === d; })[0]; }
        function evaluate(list) {
            var seen = {};
            list = list.filter(function (l) { if (!l || l.key in scored || seen[l.key]) return false; seen[l.key] = true; return true; });
            return Promise.all(list.map(function (ln) { return self.proxyAt(ln, LANE_QS); })).then(function () {
                // A lane whose budget point lies outside the strengths just measured gets one more
                // proxy right there: size curves bend past them (a photo clip fell 26 %/q over
                // q 1.5-5 and 6 %/q past 6), and the extrapolation promised reach that was not there.
                return Promise.all(list.map(function (ln) {
                    var q = self.qAt(ln, target);
                    return q != null && self.outside(ln, q) ? self.proxyAt(ln, [clamp(q, Q_MIN, Q_MAX)]) : null;
                }));
            }).then(function () {
                list.forEach(function (ln) { scored[ln.key] = self.scoreAt(ln, target); });
            });
        }
        function bestOf(list) {
            var b = null;
            list.forEach(function (l) { var v = l ? scored[l.key] : null; if (v != null && (!b || v > scored[b.key])) b = l; });
            return b;
        }
        function deeper(from) {
            // follow the frame-size family down while its smallest member is still its best
            // (or nothing reaches the budget yet)
            var tried = scales.filter(function (l) { return l.key in scored; });
            var smallest = tried[tried.length - 1];
            if (!smallest || from >= scales.length) return Promise.resolve();
            var reachable = tried.some(function (l) { return scored[l.key] != null; });
            if (reachable && bestOf(tried) !== smallest) return Promise.resolve();
            return evaluate(scales.slice(from, from + 2)).then(function () { return deeper(from + 2); });
        }
        var wave1 = scales.slice(0, 3).concat([find(1, 64, DITHER), find(1, 64, 0), find(1, 255, 0)]);
        return evaluate(wave1).then(function () {
            return deeper(3);
        }).then(function () {
            // the palette family: its neighbours only when 64 colours led, or nothing reaches yet
            var p64 = bestOf([find(1, 64, DITHER), find(1, 64, 0)]);
            var all = Object.keys(scored).map(function (k) { return scored[k]; }).filter(function (v) { return v != null; });
            var lead = Math.max.apply(null, all.concat([s2base == null ? -Infinity : s2base]));
            if (all.length && !(p64 && scored[p64.key] >= lead)) return;
            var d = p64 ? p64.diffuse : DITHER;
            return evaluate([find(1, 32, d), find(1, 128, d), find(1, 32, 0)]);
        });
    };

    /**
     * Refine lanes on the real file, the best PREDICTED first — re-predicted after
     * every refine, since each real encode recalibrates its family (skew). The full
     * lane gets a head start of LANE_GAIN. After the first, a lane is tried only
     * while it promises clearly more (LANE_GAIN) than the best result MEASURED so
     * far; at most MAX_LANES. A lane the proxy mispredicted simply measures worse.
     */
    Search.prototype.tryLanes = function (pool, target, lanes) {
        var self = this, done = {}, tried = 0;
        function pick() {
            var c = null;
            pool.forEach(function (ln) {
                if (done[ln.key]) return;
                var v = self.scoreAt(ln, target);
                if (v == null) return;
                if (ln.key === BASE.key) v += LANE_GAIN;
                if (!c || v > c.s2) c = { lane: ln, s2: v };
            });
            // nothing promises to reach the budget: the full lane still gets its walk to full strength
            if (!c && !tried && pool.indexOf(BASE) >= 0) c = { lane: BASE, s2: -Infinity };
            return c;
        }
        function next() {
            if (self.cancelled() || tried >= MAX_LANES || (self.best && self.encodes >= self.maxFull)) return Promise.resolve();
            var c = pick(), b = self.best;
            if (!c || (tried > 0 && b && b.s2 != null && b.s2 >= c.s2 - LANE_GAIN)) return Promise.resolve();
            done[c.lane.key] = true; tried++;
            self.progress(0.35 + 0.12 * tried, 'refine');
            return self.refine(c.lane, target).then(next);
        }
        return next().then(function () {
            if (self.best || self.cancelled()) return self.finish();
            return self.fallback(target, lanes);
        });
    };

    /**
     * Nothing tried reached the budget (a proxy can promise reach a lane does not
     * have). Walk down the ladder of ever smaller lanes at full strength, each step
     * aimed by the size just measured (bytes ~ pixel count); in the first lane that
     * fits, fill the budget back up — and in the next smaller one too, since a
     * smaller frame at a weaker strength may look better. The better measured wins.
     * (Before 2026-10-08 this took the SMALLEST lane and gave a sticker asked for
     * 0.25 of its size 0.07 at a score of -28.)
     */
    Search.prototype.fallback = function (target, lanes) {
        var self = this;
        var ladder = lanes.filter(function (l) { return l.scale < 1; }).sort(function (a, b) { return b.scale - a.scale; });
        var byArea = ladder.length > 0;
        if (!byArea) ladder = lanes.filter(function (l) { return l.K < 255; }).sort(function (a, b) { return (b.K - a.K) || (b.diffuse - a.diffuse); });
        var smallest = null;
        Object.keys(this.full).forEach(function (k) { self.full[k].forEach(function (p) { if (!smallest || p.size < smallest.size) smallest = p; }); });
        function aim(from, pt) {
            // first ladder index whose frame is small enough for the size `pt` measured
            if (!byArea || !pt) return from;
            var want = pt.lane.scale * Math.sqrt(target / pt.size);
            var i = from;
            while (i < ladder.length - 1 && ladder[i].scale > want) i++;
            return i;
        }
        function atMax(ln) {
            var known = (self.full[ln.key] || []).filter(function (p) { return p.q >= Q_MAX - 0.01; })[0];
            return known ? Promise.resolve(known) : self.fullAt(ln, Q_MAX, target);
        }
        function unreachable() { return { unreachable: true, minSize: smallest ? smallest.size : null, encodes: self.encodes }; }
        function step(i) {
            if (self.cancelled()) return Promise.resolve(null);
            if (i >= ladder.length) return Promise.resolve(unreachable());
            var ln = ladder[i];
            return atMax(ln).then(function (pt) {
                if (!smallest || pt.size < smallest.size) smallest = pt;
                if (pt.size > target) return step(Math.max(i + 1, aim(i + 1, pt)));
                return self.refine(ln, target).then(function () {
                    return i + 1 < ladder.length && !self.cancelled() ? self.refine(ladder[i + 1], target) : null;
                }).then(function () { return self.finish(); });
            });
        }
        if (byArea || !ladder.length) return step(aim(0, smallest));
        // the palette ladder moves in small steps: its smallest member at full strength first —
        // if even that overshoots, nothing below the frame size the user kept will fit
        return atMax(ladder[ladder.length - 1]).then(function (pt) {
            if (!smallest || pt.size < smallest.size) smallest = pt;
            return pt.size > target ? unreachable() : step(0);
        });
    };

    /**
     * Secant steps on the real file toward the WEAKEST strength that fits (≤ 4
     * encodes in the lane). Once the lane has two real points, its own slope steers:
     * the proxy's curve only aims the first step — at high strength it is far
     * steeper than the file's (a photo clip: −26 %/q on the proxy, −7 %/q in the
     * file), and following it crept toward the budget one encode at a time.
     */
    Search.prototype.refine = function (ln, target) {
        var self = this, steps = 0, aim = target * 0.97;
        function step() {
            if (self.cancelled() || steps >= 4 || (self.best && self.encodes >= self.maxFull)) return Promise.resolve();
            var pts = self.full[ln.key] || [];
            var over = null, under = null;
            pts.forEach(function (p) {
                if (p.size > target) { if (!over || p.q > over.q) over = p; }
                else if (!under || p.q < under.q) under = p;
            });
            if (under && over && under.q - over.q < 0.08) return Promise.resolve();
            // the lane's own slope (log size per unit q) from its two real points nearest the aim
            var near = pts.slice().sort(function (a, b) { return Math.abs(Math.log(a.size / aim)) - Math.abs(Math.log(b.size / aim)); }).slice(0, 2);
            var k = near.length === 2 && Math.abs(near[1].q - near[0].q) > 0.01 ? (Math.log(near[1].size) - Math.log(near[0].size)) / (near[1].q - near[0].q) : null;
            if (k != null && k > -1e-3) k = null;          // size must fall as strength grows; noise says nothing
            function sizeAt(q) { return Math.exp(Math.log(near[0].size) + k * (q - near[0].q)); }
            var q;
            if (over && under) {
                // Done when the fitting result fills the budget AND sits close to the strength at
                // which the file meets the budget exactly (q*, from the bracket). Filling alone is
                // not enough: where the size curve is flat (posterised art) a much weaker — better-
                // looking — strength lands on the same size. Closer than ~8 % of q is not worth an
                // encode: on a modelled clip the old rule crept 2.17 → 2.13 → 2.09 → 2.05 for 0.3 %.
                var qStar = qForSize([over, under], target);
                if (under.size >= target * self.fill && (qStar == null || under.q - qStar <= Math.max(0.1, under.q * 0.08))) return Promise.resolve();
                q = qForSize([over, under], aim);
                q = clamp(q == null ? (over.q + under.q) / 2 : q, over.q + 0.04, under.q - 0.04);
            } else if (under && under.size >= target * self.fill) {
                // fills the budget, nothing weaker known: probe well below — unless the lane's own
                // slope (or, with one real point, the proxy's curve through it) says that overshoots
                q = Math.max(Q_MIN, under.q * 0.45);
                if (under.q - q < 0.08) return Promise.resolve();
                var pred = k != null ? null : self.predicted(ln);
                var guess = k != null ? sizeAt(q) : interp(pred, q, 'size');
                // a long clip: no whole-file pass on a probe its sample's curve cannot vouch for (one point is no curve)
                if (self.encodeSpan && (guess == null || (k == null && (!pred || pred.length < 2)))) return Promise.resolve();
                if (guess != null && guess > target * (k != null ? Math.min(1.1, self.slack) : self.slack)) return Promise.resolve();
            } else if (over && over.q >= Q_MAX) {
                return Promise.resolve();                  // full strength overshoots: this lane cannot
            } else if (k != null) {
                // two real points on one side of the budget: follow them
                q = near[0].q + (Math.log(aim) - Math.log(near[0].size)) / k;
                if (over && q > Q_MAX && sizeAt(Q_MAX) > target * 1.03) return Promise.resolve();   // out of reach
                if (over && q <= over.q) q = Math.min(Q_MAX, over.q + 0.75);
                if (under && q >= under.q) q = Math.max(Q_MIN, under.q * 0.6);
            } else {
                // first step(s): the proxy's curve through what the lane has measured
                var pred = self.predicted(ln) || [];
                var curve = pts.concat(pred.filter(function (x) { return !pts.some(function (p) { return Math.abs(p.q - x.q) < 0.01; }); }));
                q = qForSize(curve, aim);
                if (q == null) q = over ? Math.min(Q_MAX, over.q * 1.6 + 0.5) : Q_MAX;
                if (over && q <= over.q) q = Math.min(Q_MAX, over.q + 0.75);
                if (under && q >= under.q) q = Math.max(Q_MIN, under.q * 0.6);
            }
            q = clamp(q, Q_MIN, Q_MAX);
            if (pts.some(function (p) { return Math.abs(p.q - q) < 0.02; })) return Promise.resolve();
            steps++;
            self.progress(0.45 + 0.1 * steps, 'refine');
            return self.fullAt(ln, q, target).then(step);
        }
        return step();
    };

    Search.prototype.finish = function () {
        var b = this.best;
        if (!b) return Promise.resolve(null);
        return Promise.resolve({ bytes: b.res.bytes, size: b.size, quality: b.res.quality, lane: b.lane, q: b.q, encodes: this.encodes, proxyEncodes: this.proxyEncodes, sampleEncodes: this.sampleEncodes, width: b.res.width, height: b.res.height });
    };

    /**
     * Quick mode: the smallest file whose measured quality stays ≥ minS2,
     * frame size and palette size untouched. A real encode is accepted down to
     * minS2 − 2 (sampled frames wobble; the page asks 72, so a delivered Quick
     * result never drops under the result badge's "high" at 70).
     */
    Search.prototype.forQuality = function (minS2, sourceSize) {
        var self = this, floor = minS2 - 2;
        var qs = [0.5, 1, 1.5, 2, 3, 4.5];
        this.progress(0.05, 'proxy');
        return this.proxyAt(BASE, qs).then(function (pts) {
            if (self.cancelled()) return null;
            var ok = pts.filter(function (p) { return p.s2 != null && p.s2 >= minS2 + 1; }).sort(function (a, b) { return b.q - a.q; });
            var q = ok.length ? ok[0].q : Q_MIN;
            var next = pts.filter(function (p) { return p.q > q; }).sort(function (a, b) { return a.q - b.q; })[0];
            if (next && next.s2 != null && ok.length) {
                var f = (ok[0].s2 - (minS2 + 1)) / ((ok[0].s2 - next.s2) || 1);
                q = q + clamp(f, 0, 1) * (next.q - q) * 0.8;
            }
            self.progress(0.4, 'encode');
            return self.fullAt(BASE, q, null).then(function (pt) {
                if (pt.s2 != null && pt.s2 < floor && q > Q_MIN) {
                    self.progress(0.75, 'encode');
                    return self.fullAt(BASE, Math.max(Q_MIN, q * 0.6), null);
                }
                return pt;
            });
        }).then(function (pt) {
            if (!pt) return null;
            var all = self.full[BASE.key].filter(function (p) { return p.s2 == null || p.s2 >= floor; });
            var best = all.sort(function (a, b) { return a.size - b.size; })[0] || pt;
            if (sourceSize && best.size >= sourceSize) return { noGain: true, encodes: self.encodes };
            return { bytes: best.res.bytes, size: best.size, quality: best.res.quality, lane: BASE, q: best.q, encodes: self.encodes, proxyEncodes: self.proxyEncodes, width: best.res.width, height: best.res.height };
        });
    };

    function create(runner, info, opts) { return new Search(runner, info, opts); }

    /** in-process runner over a GifCodec.Session (Node tests, or a single worker) */
    function sessionRunner(session) {
        // the plan first: the scan behind proxy() keeps the decoder states its runs start from
        var sample = session.samplePlan ? session.samplePlan() : null;
        return {
            parallel: 1,
            proxyShare: session.proxy().sourceShare,
            sample: sample,
            encode: function (params, o) {
                return new Promise(function (resolve) {
                    if (o && o.proxy) return resolve(session.encodeProxy(params));
                    if (!(o && o.sample && sample)) return resolve(session.encode(params, { measure: true }));
                    var size = 0, s2 = 0, n = 0, last = null;
                    sample.runs.forEach(function (r) {
                        last = session.encode(params, { measure: true, from: r[0], to: r[1], warm: true });
                        size += last.size;
                        if (last.quality && last.quality.s2 != null) { s2 += last.quality.s2; n++; }
                    });
                    resolve({ size: size, quality: { s2: n ? s2 / n : null, de: null }, width: last.width, height: last.height });
                });
            }
        };
    }

    return { create: create, sessionRunner: sessionRunner, qForSize: qForSize, Q_MIN: Q_MIN, Q_MAX: Q_MAX };
}));
